import React, { ReactElement, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { Image } from 'components/lib';
import {
  COLORS,
  FONT,
  ViewIcon,
  ManagerIcon,
  RoleBadge,
  OrgRole,
  SkeletonBlock,
  initialsOf,
} from '../shared';

export interface OrgTileProps {
  name: string;
  logoUrl?: string;
  role: OrgRole;
  onView: () => void;

  onManage?: () => void;
}

const CREST_SIZE = 100;

export const OrgTile = ({
  name,
  logoUrl,
  role,
  onView,
  onManage,
}: OrgTileProps): ReactElement => {
  const [logoError, setLogoError] = useState(false);
  const showLogo = logoUrl && !logoError;
  const canManage = Boolean(onManage);

  return (
    <Box
      sx={{
        backgroundColor: COLORS.white,
        border: `1px solid ${COLORS.cardBorder}`,
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        height: '100%',
        pt: '18px',
        pb: '14px',
        px: '16px',
        width: '100%',
        transition: 'box-shadow 200ms ease',
        '&:hover': { boxShadow: '0 4px 16px rgba(20,31,51,0.08)' },
      }}
    >
      <ButtonBase
        onClick={onView}
        disableRipple
        aria-label={`View ${name}`}
        sx={{
          width: `${CREST_SIZE}px`,
          height: `${CREST_SIZE}px`,
          borderRadius: '12px',
          backgroundColor: COLORS.imagePlaceholder,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          '&:focus-visible': {
            outline: `2px solid ${COLORS.navy}`,
            outlineOffset: '2px',
          },
        }}
      >
        {showLogo ? (
          <Image
            src={logoUrl ?? ''}
            alt={name}
            width={CREST_SIZE}
            height={CREST_SIZE}
            onError={() => setLogoError(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Typography
            aria-hidden
            sx={{
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: '32px',
              color: COLORS.navy,
            }}
          >
            {initialsOf(name)}
          </Typography>
        )}
      </ButtonBase>

      <Box
        sx={{
          flex: '1 0 0',
          minWidth: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          alignSelf: 'stretch',
        }}
      >
        <Box sx={{ alignSelf: 'flex-start' }}>
          <RoleBadge role={role} />
        </Box>
        <Typography
          component={ButtonBase}
          onClick={onView}
          disableRipple
          sx={{
            fontFamily: FONT,
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: 1.32,
            color: COLORS.headingDark,
            width: '100%',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            textAlign: 'left',
            justifyContent: 'flex-start',
            '&:hover': { textDecoration: 'underline' },
            '&:focus-visible': {
              outline: `2px solid ${COLORS.navy}`,
              outlineOffset: '2px',
            },
          }}
        >
          {name}
        </Typography>

        <Box
          sx={{
            mt: 'auto',
            backgroundColor: COLORS.toolbarBg,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
          }}
        >
          <ButtonBase
            onClick={onView}
            disableRipple
            sx={{
              flex: '1 0 0',
              minWidth: 0,
              height: '48px',
              gap: '6px',
              color: COLORS.navy,
              '&:hover': {
                backgroundColor: COLORS.hoverRow,
                '& .org-tile-action-label': { textDecoration: 'underline' },
              },
              '&:focus-visible': {
                outline: `2px solid ${COLORS.navy}`,
                outlineOffset: '-2px',
              },
            }}
          >
            <ViewIcon color={COLORS.navy} />
            <Typography
              className="org-tile-action-label"
              sx={{
                fontFamily: FONT,
                fontWeight: 500,
                fontSize: '13px',
                color: COLORS.navy,
              }}
            >
              View
            </Typography>
          </ButtonBase>
          {canManage && (
            <>
              <Box
                sx={{
                  width: '1px',
                  height: '14px',
                  backgroundColor: COLORS.divider,
                }}
              />
              <ButtonBase
                onClick={onManage}
                disableRipple
                sx={{
                  flex: '1 0 0',
                  minWidth: 0,
                  height: '48px',
                  gap: '6px',
                  color: COLORS.navy,
                  '&:hover': {
                    backgroundColor: COLORS.hoverRow,
                    '& .org-tile-action-label': { textDecoration: 'underline' },
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${COLORS.navy}`,
                    outlineOffset: '-2px',
                  },
                }}
              >
                <ManagerIcon color={COLORS.navy} />
                <Typography
                  className="org-tile-action-label"
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 500,
                    fontSize: '13px',
                    color: COLORS.navy,
                  }}
                >
                  Manage
                </Typography>
              </ButtonBase>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const OrgTileSkeleton = (): ReactElement => (
  <Box
    sx={{
      backgroundColor: COLORS.white,
      border: `1px solid ${COLORS.cardBorder}`,
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
      pt: '18px',
      pb: '14px',
      px: '16px',
      width: '100%',
    }}
  >
    <SkeletonBlock
      width={100}
      height={100}
      radius={12}
      sx={{ flexShrink: 0 }}
    />
    <Box flex="1 0 0" display="flex" flexDirection="column" gap="8px">
      <SkeletonBlock width={56} height={22} radius={9999} />
      <SkeletonBlock width="80%" height={18} />
      <SkeletonBlock width="100%" height={48} />
    </Box>
  </Box>
);
